import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, ExternalLink, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PaymentVerificationPanel = ({ formatCurrency }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/payment-verifications`, { headers: headers() });
      setItems(res.data.verifications || []);
    } catch (e) {
      // 403 when user lacks permission — hide silently
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const approve = async (orderId) => {
    setActingId(orderId);
    try {
      const res = await axios.post(`${API_BASE_URL}/payment-verifications/${orderId}/approve`, {}, { headers: headers() });
      const shop = res.data.shopify;
      toast.success(`Payment approved${shop ? (shop.success ? ' — Shopify order marked paid' : ` (Shopify sync: ${shop.message || 'failed'})`) : ''}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve payment');
    } finally {
      setActingId(null);
    }
  };

  const reject = async (orderId) => {
    if (!rejectReason.trim()) { toast.error('Enter a rejection reason'); return; }
    setActingId(orderId);
    try {
      await axios.post(`${API_BASE_URL}/payment-verifications/${orderId}/reject`, { reason: rejectReason.trim() }, { headers: headers() });
      toast.success('Payment rejected — order creator notified');
      setRejectingId(null);
      setRejectReason('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reject payment');
    } finally {
      setActingId(null);
    }
  };

  const pending = items.filter(i => i.payment_verification?.status === 'pending');
  const rejected = items.filter(i => i.payment_verification?.status === 'rejected');

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.25 }}
      className="bg-wt-paper border border-wt-border rounded-xl p-6"
      data-testid="payment-verifications-panel"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 rounded-lg bg-purple-500/10">
          <ShieldCheck className="h-6 w-6 text-purple-400" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-wt-text-primary">Payment Verifications</h2>
          <p className="text-wt-text-muted text-sm">Orders created as "paid" with uploaded proof — approve to mark paid (incl. Shopify)</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} className="border-wt-border text-wt-text-muted" data-testid="refresh-verifications-btn">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Badge className="bg-purple-500/20 text-purple-400 border border-purple-500/30" data-testid="pending-verifications-count">
            {pending.length} Pending
          </Badge>
        </div>
      </div>

      {loading ? (
        <p className="text-wt-text-muted text-center py-4">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-wt-text-muted text-center py-4" data-testid="no-verifications-message">No payments awaiting verification</p>
      ) : (
        <div className="space-y-3">
          {[...pending, ...rejected].map((o) => {
            const pv = o.payment_verification || {};
            const isRejected = pv.status === 'rejected';
            return (
              <div key={o.id} className="p-4 bg-wt-bg rounded-lg border border-wt-border" data-testid={`verification-row-${o.order_number}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-wt-text-primary">#{o.order_number} — {o.customer_name}</p>
                      <Badge className={isRejected
                        ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                        : 'bg-purple-500/20 text-purple-400 border border-purple-500/30'}>
                        {isRejected ? 'Rejected — awaiting new proof' : 'Pending'}
                      </Badge>
                    </div>
                    <p className="text-xs text-wt-text-muted mt-1">
                      {formatCurrency ? formatCurrency(o.total_amount) : o.total_amount} · {pv.payment_method_name || '—'} · submitted by {pv.submitted_by_name || o.created_by_name || '—'}
                    </p>
                    {isRejected && pv.rejection_reason && (
                      <p className="text-xs text-red-400 mt-1">Reason: {pv.rejection_reason}</p>
                    )}
                    {pv.proof_url && (
                      <a href={pv.proof_url} target="_blank" rel="noreferrer"
                        className="text-xs text-wt-gold hover:underline inline-flex items-center gap-1 mt-1"
                        data-testid={`proof-link-${o.order_number}`}>
                        View payment proof <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                  {!isRejected && (
                    <div className="flex gap-2 shrink-0">
                      <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white"
                        disabled={actingId === o.id}
                        onClick={() => approve(o.id)}
                        data-testid={`approve-payment-${o.order_number}`}>
                        Approve
                      </Button>
                      <Button size="sm" variant="outline" className="border-red-500/40 text-red-400 hover:bg-red-500/10"
                        disabled={actingId === o.id}
                        onClick={() => { setRejectingId(rejectingId === o.id ? null : o.id); setRejectReason(''); }}
                        data-testid={`reject-payment-${o.order_number}`}>
                        Reject
                      </Button>
                    </div>
                  )}
                </div>
                {rejectingId === o.id && (
                  <div className="mt-3 flex items-center gap-2">
                    <Input
                      placeholder="Rejection reason (required)"
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      className="bg-wt-surface border-wt-border text-wt-text-primary text-sm"
                      data-testid={`reject-reason-input-${o.order_number}`}
                    />
                    <Button size="sm" variant="outline" className="border-red-500/40 text-red-400 hover:bg-red-500/10 shrink-0"
                      disabled={actingId === o.id}
                      onClick={() => reject(o.id)}
                      data-testid={`confirm-reject-${o.order_number}`}>
                      Confirm Reject
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </motion.div>
  );
};

export default PaymentVerificationPanel;
